import { useState } from "react";
import { Pin, PinOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePins } from "@/lib/pins-context";
import { ApiError } from "@/lib/api";
import type { PinType, PinEntityType } from "@dugads/shared";

interface PinButtonProps {
  pinType: PinType;
  entityType: PinEntityType;
  entityId: string;
}

export function PinButton({ pinType, entityType, entityId }: PinButtonProps) {
  const { isPinned, addPin, removePin, pins } = usePins();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pinned = isPinned(entityType, entityId);
  const existingPin = pins.find(
    (p) => p.entity_type === entityType && p.entity_id === entityId,
  );

  async function handleToggle() {
    setLoading(true);
    setError(null);
    try {
      if (pinned && existingPin) {
        await removePin(existingPin.id);
      } else {
        await addPin(pinType, entityType, entityId);
      }
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "Failed to update pin";
      setError(msg);
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1.5"
        onClick={handleToggle}
        disabled={loading}
        title={pinned ? "Unpin from sidebar" : "Pin to sidebar"}
      >
        {pinned ? (
          <>
            <PinOff className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Unpin</span>
          </>
        ) : (
          <>
            <Pin className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Pin</span>
          </>
        )}
      </Button>
      {error && (
        <span className="text-xs text-destructive">{error}</span>
      )}
    </div>
  );
}
